import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Key, X, Info } from "lucide-react";
import Modal from "components/common/Modal";

// Privilege values are lowercased and sent to the API; labels are translated
const PRIVILEGE_OPTIONS = ["Read", "Write"];
const RESOURCE_OPTIONS = [
  { labelKey: "teams", value: "teams" },
  { labelKey: "athletes", value: "players" },
  { labelKey: "sessions", value: "sessions" },
];

const CreateApiKeyModal = ({ show, onClose, onCreate }) => {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [privileges, setPrivileges] = useState([]);
  const [resources, setResources] = useState([]);
  const [creating, setCreating] = useState(false);

  const handleClose = () => {
    setName("");
    setPrivileges([]);
    setResources([]);
    setCreating(false);
    onClose();
  };

  const toggleItem = (list, setList, item) => {
    setList((prev) =>
      prev.includes(item) ? prev.filter((i) => i !== item) : [...prev, item]
    );
  };

  const isValid =
    name.trim().length > 0 && privileges.length > 0 && resources.length > 0;

  const handleCreate = async () => {
    if (!isValid || creating) return;
    setCreating(true);
    try {
      await onCreate({
        name: name.trim(),
        permissions: {
          privileges: privileges.map((p) => p.toLowerCase()),
          resources,
        },
      });
      handleClose();
    } catch {
      setCreating(false);
    }
  };

  return (
    <Modal show={show} onClose={handleClose} maxWidth="480px">
      {/* Header */}
      <div
        className="relative overflow-hidden p-7"
        style={{
          background: "linear-gradient(135deg, #E8854A 0%, #D66B2E 100%)",
        }}
      >
        <div
          className="absolute rounded-full"
          style={{
            width: 120,
            height: 120,
            top: -30,
            right: -30,
            background: "rgba(255, 255, 255, 0.1)",
          }}
        />
        <div
          className="absolute rounded-full"
          style={{
            width: 80,
            height: 80,
            bottom: -40,
            right: 60,
            background: "rgba(255, 255, 255, 0.08)",
          }}
        />

        <div className="relative flex items-start justify-between">
          <div>
            <div
              className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl"
              style={{ background: "rgba(255, 255, 255, 0.2)" }}
            >
              <Key size={24} className="text-white" />
            </div>
            <h2 className="text-xl font-bold text-white">
              {t("workspace.createApiKeyModal.title")}
            </h2>
            <p
              className="mt-1.5 text-sm"
              style={{ color: "rgba(255, 255, 255, 0.8)" }}
            >
              {t("workspace.createApiKeyModal.subtitle")}
            </p>
          </div>

          <button
            onClick={handleClose}
            className="flex h-9 w-9 items-center justify-center rounded-[10px] transition-colors duration-150"
            style={{ background: "rgba(255, 255, 255, 0.2)" }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background = "rgba(255, 255, 255, 0.3)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)")
            }
          >
            <X size={18} className="text-white" />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="p-7">
        {/* Name Field */}
        <div className="mb-6">
          <label className="mb-2 block text-sm font-medium text-[#374151]">
            {t("workspace.createApiKeyModal.nameLabel")}
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("workspace.createApiKeyModal.namePlaceholder")}
            className="w-full rounded-[10px] border border-[#E5E7EB] px-4 py-3 text-sm text-[#111827] placeholder-[#9CA3AF] outline-none transition-all duration-150"
            style={{ boxShadow: "none" }}
            onFocus={(e) => {
              e.target.style.borderColor = "#E07A3A";
              e.target.style.boxShadow = "0 0 0 3px rgba(224, 122, 58, 0.1)";
            }}
            onBlur={(e) => {
              e.target.style.borderColor = "#E5E7EB";
              e.target.style.boxShadow = "none";
            }}
          />
        </div>

        {/* Privileges */}
        <div className="mb-6">
          <label className="mb-2 block text-sm font-medium text-[#374151]">
            {t("workspace.createApiKeyModal.privilegesLabel")}
          </label>
          <div className="flex gap-2">
            {PRIVILEGE_OPTIONS.map((opt) => {
              const selected = privileges.includes(opt);
              return (
                <button
                  key={opt}
                  onClick={() => toggleItem(privileges, setPrivileges, opt)}
                  className="rounded-[10px] border px-4 py-2.5 text-sm font-medium transition-all duration-150"
                  style={
                    selected
                      ? {
                          borderColor: "#E07A3A",
                          background: "rgba(224, 122, 58, 0.08)",
                          color: "#E07A3A",
                        }
                      : {
                          borderColor: "#E5E7EB",
                          background: "white",
                          color: "#6B7280",
                        }
                  }
                >
                  {t(
                    `workspace.createApiKeyModal.privileges.${opt.toLowerCase()}`
                  )}
                </button>
              );
            })}
          </div>
          <p className="mt-1.5 text-xs text-[#9CA3AF]">
            {t("workspace.createApiKeyModal.privilegesHint")}
          </p>
        </div>

        {/* Resources */}
        <div className="mb-7">
          <label className="mb-2 block text-sm font-medium text-[#374151]">
            {t("workspace.createApiKeyModal.resourcesLabel")}
          </label>
          <div className="flex gap-2">
            {RESOURCE_OPTIONS.map((opt) => {
              const selected = resources.includes(opt.value);
              return (
                <button
                  key={opt.value}
                  onClick={() => toggleItem(resources, setResources, opt.value)}
                  className="rounded-[10px] border px-4 py-2.5 text-sm font-medium transition-all duration-150"
                  style={
                    selected
                      ? {
                          borderColor: "#E07A3A",
                          background: "rgba(224, 122, 58, 0.08)",
                          color: "#E07A3A",
                        }
                      : {
                          borderColor: "#E5E7EB",
                          background: "white",
                          color: "#6B7280",
                        }
                  }
                >
                  {t(`workspace.createApiKeyModal.resources.${opt.labelKey}`)}
                </button>
              );
            })}
          </div>
          <p className="mt-1.5 text-xs text-[#9CA3AF]">
            {t("workspace.createApiKeyModal.resourcesHint")}
          </p>
        </div>

        {/* Info Box */}
        <div
          className="flex gap-3 rounded-xl p-4"
          style={{
            background:
              "linear-gradient(135deg, rgba(232, 133, 74, 0.08) 0%, rgba(232, 133, 74, 0.03) 100%)",
            border: "1px solid rgba(232, 133, 74, 0.15)",
          }}
        >
          <Info size={20} className="mt-0.5 flex-shrink-0 text-[#E07A3A]" />
          <p className="text-[13px] leading-relaxed text-[#6B7280]">
            {t("workspace.createApiKeyModal.info")}
          </p>
        </div>
      </div>

      {/* Footer */}
      <div className="flex justify-end gap-3 border-t border-[#F3F4F6] px-7 py-5">
        <button
          onClick={handleClose}
          className="rounded-[10px] border border-[#E5E7EB] bg-white px-5 py-2.5 text-sm font-medium text-[#374151] transition-colors duration-150 hover:bg-[#F9FAFB]"
        >
          {t("common.actions.cancel")}
        </button>
        <button
          onClick={handleCreate}
          disabled={!isValid || creating}
          className="rounded-[10px] px-5 py-2.5 text-sm font-semibold text-white transition-all duration-150"
          style={
            isValid && !creating
              ? {
                  background:
                    "linear-gradient(135deg, #E8854A 0%, #D66B2E 100%)",
                  boxShadow: "0 2px 8px rgba(224, 122, 58, 0.3)",
                  cursor: "pointer",
                }
              : {
                  background: "#F3F4F6",
                  color: "#9CA3AF",
                  cursor: "not-allowed",
                }
          }
        >
          {creating
            ? t("workspace.shared.creating")
            : t("workspace.createApiKeyModal.submit")}
        </button>
      </div>
    </Modal>
  );
};

export default CreateApiKeyModal;
